import {ceil} from 'lodash';
import React, {useState} from 'react';
import {useMedia} from 'react-use';
import {useCachedState} from '../use-cached-state';
import {Media, MediaSizes} from '../../styles/breakpoints';
import {
  Group,
  Pagination as MantinePagination,
  Select,
  Text,
} from '@mantine/core';
import {css} from '@emotion/react';

type InitialPaginationState = {
  initialPage?: number;
  initialPageSize?: number;
};

type PaginationResult = {
  pageNumber: number;
  pageSize: number;
  render: (totalPages: number, totalRows: number) => React.ReactElement;
};

const defaultPageState = {
  initialPage: 1,
  initialPageSize: 25,
};

const pageSizeOptions = [
  {key: 1, label: '10', value: 10},
  {key: 2, label: '25', value: 25},
  {key: 3, label: '50', value: 50},
];

export const usePagination = (
  initialState?: InitialPaginationState
): PaginationResult => {
  const defaultState = initialState
    ? {
      ...defaultPageState,
      ...initialState,
    }
    : defaultPageState;

  const [pageNumber, setPageNumber] = useState(defaultState.initialPage);
  const [pageSize, setPageSize] = useCachedState(
    defaultState.initialPageSize,
    'lines-per-page'
  );

  const useSmallPagination = useMedia(`(${MediaSizes.MobileMax})`);
  const useMediumPagination = useMedia(`(${MediaSizes.TabletMax})`);

  let paginationProps = {};

  const render = (totalPages: number, totalRows: number) => {
    totalPages = ceil(totalPages);
    const maxPage = Math.min(pageNumber, totalPages);
    const pageStart = Math.max((maxPage - 1) * pageSize + 1, 1);
    const pageStop = Math.min(maxPage * pageSize, totalRows);

    if (useMediumPagination) {
      paginationProps = {
        ...paginationProps,
        boundaryRange: 0,
        ellipsisItem: null,
      };
    }

    if (useSmallPagination) {
      paginationProps = {
        ...paginationProps,
        fluid: true,
        widths: totalPages > 3 ? 5 : totalPages + 2,
      };
    }

    return (
      <div css={styles} className="pagination">
        <Group justify="space-between">
          {totalPages > 0 && (
            <>
              <MantinePagination
                total={totalPages}
                value={maxPage}
                onChange={(page) => {
                  setPageNumber(page);
                }}
                // color={FigmaColors.black10}
              />
            </>
          )}

          <Group>
            {totalRows > 0 && (
              <Group>
                <Text
                  className="current-page"
                  size={'14px'}
                >{`Showing ${pageStart} - ${pageStop} of ${totalRows}`}</Text>
                <Select
                  css={css`
                    max-width: 5rem;
                  `}
                  data={pageSizeOptions.map((x) => x.label)}
                  value={pageSize.toString()}
                  onChange={(value) => {
                    setPageSize(parseInt(value as string));
                  }}
                />
              </Group>
            )}
          </Group>
        </Group>
      </div>
    );
  };

  return {
    pageNumber,
    pageSize,
    render,
  };
};

const styles = css`
  .lines-per-page {
    display: inline-flex;
    align-items: center;

    ${Media('MobileMax')} {
      display: flex;

      .current-page {
        flex: 1 1 0%;
      }
    }

    .ui.dropdown {
      margin-left: 20px;
    }
  }

  .ui.pagination.menu .item {
    &.disabled:focus {
      outline: none !important;
    }
  }
`;